/**
 * The shell handshake, spoken exactly as `@activekit/js` implements it.
 *
 * The protocol is not this repository's to invent: `packages/js/src/shell.ts`
 * in `activekit-js` is the other end, and `docs/contracts/shell.md` is the
 * agreement. Both are the reason for every constant below.
 *
 * The shape that matters most is the version tag. The shell drops any message
 * whose `v` is not the protocol number, silently, so a `ready` without it is a
 * handshake that never happens: the shell times out and paints its offline
 * state while this app waits for a token that was never going to arrive.
 *
 * The token is minted by the customer's server through the server SDK, is
 * short lived, is scoped to `/v1/me/*`, and reaches this app only over
 * postMessage. It never appears in the URL: a token in the address bar is a
 * token in browser history, in a referrer header, and in every proxy log on
 * the way.
 */

/** Must match `PROTOCOL` in `packages/js/src/shell.ts`. */
const PROTOCOL = 1;

/** What the shell sends to this app. */
type ShellMessage =
  | { v: number; type: "init"; token: string; theme?: "light" | "dark"; locale?: string }
  | { v: number; type: "token"; token: string }
  | { v: number; type: "theme"; theme: "light" | "dark" };

export interface Session {
  token: string;
  apiBaseUrl: string;
}

/**
 * The API's address is a build time value rather than something the shell
 * sends, because each environment deploys its own bundle and because a base
 * URL a host page could set is a base URL a host page could point at itself.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.activekit.app";

function isShellMessage(data: unknown): data is ShellMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { v?: unknown }).v === PROTOCOL &&
    typeof (data as { type?: unknown }).type === "string"
  );
}

/** Post to the shell, always version tagged, or the shell will not listen. */
function post(message: Record<string, unknown>): void {
  if (window.parent === window) return;
  // The shell checks the origin of what it receives, so a wildcard target here
  // is answered only by a frame parent that already knows this app's origin.
  window.parent.postMessage({ v: PROTOCOL, ...message }, "*");
}

export interface ShellConnection {
  session: Session;
  /** Called with each rotated token, so a long session never goes stale. */
  onToken(listener: (token: string) => void): () => void;
}

/**
 * Announce readiness and resolve when the shell answers with `init`.
 *
 * Rejects rather than hanging: a frame that waits forever renders chrome the
 * player cannot act on, and a timeout is a state this app can draw.
 */
let connection: Promise<ShellConnection> | null = null;

/**
 * There is exactly one shell, so there is exactly one handshake.
 *
 * The singleton is not a micro optimisation. `ready` is what makes the shell
 * post `init` and fire the host page's `onOpen`, so a second `ready` from a
 * remounted component makes a customer's own callback run twice for one open.
 * React's strict mode double invokes effects in development, which is exactly
 * the case that would have shipped this.
 */
export function connectShell(timeoutMs = 10_000): Promise<ShellConnection> {
  connection ??= openConnection(timeoutMs);
  return connection;
}

function openConnection(timeoutMs: number): Promise<ShellConnection> {
  return new Promise((resolve, reject) => {
    if (window.parent === window) {
      connection = null;
      reject(new Error("No shell: this app runs inside the ActiveKit frame."));
      return;
    }

    const listeners = new Set<(token: string) => void>();
    let settled = false;

    const timer = window.setTimeout(() => {
      if (settled) return;
      window.removeEventListener("message", onMessage);
      connection = null;
      reject(new Error("The shell did not send a session in time."));
    }, timeoutMs);

    function onMessage(event: MessageEvent) {
      // The shell's origin is the customer's page and differs per tenant, so
      // it cannot be pinned to one value. What is checked is the source frame,
      // the version tag and the shape; the token itself is verified by the
      // platform on every request, which is the gate that actually holds.
      if (event.source !== window.parent) return;
      if (!isShellMessage(event.data)) return;
      const message = event.data;

      switch (message.type) {
        case "init":
          window.clearTimeout(timer);
          settled = true;
          resolve({
            session: { token: message.token, apiBaseUrl: API_BASE_URL },
            onToken(listener) {
              listeners.add(listener);
              return () => listeners.delete(listener);
            },
          });
          break;

        case "token":
          // A rotation, in place, with no reload. Every listener gets it.
          for (const listener of listeners) listener(message.token);
          break;

        case "theme":
          // Deliberately ignored. The template wins: the shell's theme styles
          // the bubble and the scrim, and never what is inside the frame
          // (`docs/decisions/open-items-closed.md`, decision 3).
          break;
      }
    }

    window.addEventListener("message", onMessage);

    // `ground` is this side of the shell contract's pending adjustment 1: the
    // shell crossfades the frame against a color it currently guesses from the
    // host theme, which is wrong whenever the template disagrees with the host.
    // Sending it costs nothing before the shell reads it, since an unknown
    // field is ignored, and closes the flash the day it does.
    post({ type: "ready", ground: groundColor() });
  });
}

/** The frame's own background, read from the template that is actually rendering. */
function groundColor(): string {
  const frame = document.querySelector<HTMLElement>(".frame");
  if (!frame) return "#0b1220";
  return getComputedStyle(frame).getPropertyValue("--ground").trim() || "#0b1220";
}

/** Tell the shell to dismiss, which is what Escape means inside the frame. */
export function requestClose(): void {
  post({ type: "escape" });
}

/** Tell the shell whether the launcher's dot should be drawn. */
export function reportBadge(unacknowledged: boolean): void {
  post({ type: "badge", value: unacknowledged });
}

/**
 * Tell the host page something happened. This is the sanctioned route for
 * interaction data: the customer's own page may consume it, and ActiveKit
 * collects nothing. It is not telemetry and never reaches us.
 */
export function notifyShell(event: string, detail?: Record<string, unknown>): void {
  post({ type: "event", event, detail });
}
